import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment-timezone';

import {Anchor, Banner} from '@albert-io/atomic';

import systemTimeOffsetStore from 'client/generic/SystemTimeOffset/SystemTimeOffset.store';
import {pluralize} from 'lib/stringUtils';
import {getModelForResourceType} from 'resources/modelRegistry';

const StatusBanner = ({className, personnelRecord, teacher, theme}) => {
  const isApproved = personnelRecord.getStatus() === 'approved';
  const isPending = personnelRecord.getStatus() === 'pending';

  const school = personnelRecord.getSchool();
  const licenseEndDate = moment(teacher.getLicenseEndDateForSchool(school.getId()));
  const hasLicense =
    teacher.getLicenseStatusForSchool(school.getId()) === true &&
    teacher
      .getMeta()
      .getSchoolLicensesInfo()
      .find((item) => item.get('school_id') === school.getId())
      ?.get('school_license_status') !== 'inactive';

  const now = systemTimeOffsetStore.getCurrentTime();
  const expiresSoon =
    licenseEndDate.isValid() && hasLicense && moment(now).add(60, 'days').isAfter(licenseEndDate);

  const expirationCountdown =
    licenseEndDate.diff(now, 'days') === 0
      ? 'tomorrow'
      : `in ${licenseEndDate.diff(now, 'days')} ${pluralize(
          'day',
          licenseEndDate.diff(now, 'days')
        )}`;

  return (
    <Banner className={classnames('status-banner', className)} theme={theme}>
      {isApproved && !hasLicense && (
        <>
          <Banner.Icon icon='exclamation-triangle' iconStyle='regular' />
          <span>{school.getName()} does not have a license.</span>
          <Anchor
            className='u-mar-l_auto'
            href='http://help.albert.io/en/articles/4467647-how-do-i-get-access-to-a-school-license'
            rel='noopener noreferrer'
            target='_blank'
            underlined
          >
            Get full access
          </Anchor>
        </>
      )}
      {isPending && hasLicense && (
        <>
          <Banner.Icon icon='exclamation-circle' iconStyle='regular' />
          <span>You are currently pending approval at {school.getName()}.</span>
          <Anchor
            className='u-mar-l_auto'
            href='https://help.albert.io/en/articles/4333411-what-should-i-do-if-i-am-pending-admin-approval'
            rel='noopener noreferrer'
            target='_blank'
            underlined
          >
            Learn more
          </Anchor>
        </>
      )}
      {isApproved && expiresSoon && (
        <>
          <Banner.Icon icon='exclamation-triangle' iconStyle='regular' />
          <span>
            {school.getName()}&apos;s license will expire {expirationCountdown}.
          </span>
          <Anchor
            className='u-mar-l_auto'
            href='http://help.albert.io/en/articles/4467647-how-do-i-get-access-to-a-school-license'
            rel='noopener noreferrer'
            target='_blank'
            underlined
          >
            Learn more
          </Anchor>
        </>
      )}
    </Banner>
  );
};

StatusBanner.propTypes = {
  className: PropTypes.string,
  personnelRecord: PropTypes.instanceOf(getModelForResourceType('school_personnel_v1')),
  teacher: PropTypes.instanceOf(getModelForResourceType('teacher_v1')),
  theme: PropTypes.string
};

export default StatusBanner;
